<template>
  <h4
    class="sticky top-0 bg-white pb-2 text-2xs font-bold uppercase text-body-color"
  >
    Carrier Addresses
  </h4>
  <div class="flex flex-col gap-4 rounded-md">
    <div
      v-for="address in $store.state.carriers?.showCarrier?.contact_information
        .addresses"
      :key="address.id"
      class="flex items-center justify-between rounded-md border px-6 py-3"
      :class="{
        'border-yellow-500 bg-yellow-200': aggregatedMatch(address),
        'border-light-gray': !aggregatedMatch(address),
      }"
    >
      <div>
        <h4 class="mb-2 text-2xs font-bold uppercase text-dark-gray">
          {{ address?.is_type?.replaceAll('_', ' ') }} Address
        </h4>
        <div class="flex gap-2">
          <svg
            v-if="address.is_type == 'mailing'"
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.66634 3.58789H13.333C14.0663 3.58789 14.6663 4.18789 14.6663 4.92122V12.9212C14.6663 13.6546 14.0663 14.2546 13.333 14.2546H2.66634C1.93301 14.2546 1.33301 13.6546 1.33301 12.9212V4.92122C1.33301 4.18789 1.93301 3.58789 2.66634 3.58789Z"
              stroke="#6D7181"
              stroke-width="1.33333"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14.6663 4.92188L7.99967 9.58854L1.33301 4.92188"
              stroke="#6D7181"
              stroke-width="1.33333"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-if="address.is_type == 'physical'"
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_2149_81656)">
              <path
                d="M14 7.58789C14 12.2546 8 16.2546 8 16.2546C8 16.2546 2 12.2546 2 7.58789C2 5.99659 2.63214 4.47047 3.75736 3.34525C4.88258 2.22003 6.4087 1.58789 8 1.58789C9.5913 1.58789 11.1174 2.22003 12.2426 3.34525C13.3679 4.47047 14 5.99659 14 7.58789Z"
                stroke="#6D7181"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M8 9.58789C9.10457 9.58789 10 8.69246 10 7.58789C10 6.48332 9.10457 5.58789 8 5.58789C6.89543 5.58789 6 6.48332 6 7.58789C6 8.69246 6.89543 9.58789 8 9.58789Z"
                stroke="#6D7181"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_2149_81656">
                <rect
                  width="16"
                  height="16"
                  fill="white"
                  transform="translate(0 0.921875)"
                />
              </clipPath>
            </defs>
          </svg>
          <svg
            v-if="address.is_type == 'dispatcher'"
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.33301 2.67188H0.583008V10.2552H9.33301V2.67188Z"
              stroke="#6D7181"
              stroke-width="1.16667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M9.33301 5.58789H11.6663L13.4163 7.33789V10.2546H9.33301V5.58789Z"
              stroke="#6D7181"
              stroke-width="1.16667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M3.20833 13.1725C4.01375 13.1725 4.66667 12.5196 4.66667 11.7142C4.66667 10.9088 4.01375 10.2559 3.20833 10.2559C2.40292 10.2559 1.75 10.9088 1.75 11.7142C1.75 12.5196 2.40292 13.1725 3.20833 13.1725Z"
              stroke="#6D7181"
              stroke-width="1.16667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M10.7913 13.1725C11.5968 13.1725 12.2497 12.5196 12.2497 11.7142C12.2497 10.9088 11.5968 10.2559 10.7913 10.2559C9.98593 10.2559 9.33301 10.9088 9.33301 11.7142C9.33301 12.5196 9.98593 13.1725 10.7913 13.1725Z"
              stroke="#6D7181"
              stroke-width="1.16667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <div class="text-xs text-body-color">
            <div
              v-if="address.street1"
              class="block"
            >
              {{ address.street1 }}
            </div>
            <div
              v-if="address.street2"
              class="block"
            >
              {{ address.street2 }}
            </div>
            <div class="block">
              {{ address.city }}, {{ address.state }} {{ address.postal_code }}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div
          v-if="aggregatedMatch(address)"
          class="flex flex-col gap-2"
        >
          <div class="flex items-center gap-2">
            <h4 class="text-2xs font-bold uppercase text-dark-gray">
              Undeliverable Address
            </h4>
            <svg
              class="-top-1px relative"
              width="16"
              height="16"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M15 8.92188C15 12.7879 11.866 15.9219 8 15.9219C4.13401 15.9219 1 12.7879 1 8.92188C1 5.05588 4.13401 1.92188 8 1.92188C11.866 1.92188 15 5.05588 15 8.92188ZM6.99992 9.75579V4.40234H8.99992V9.75579H6.99992ZM6.99992 11.6676V13.8808H8.99992V11.6676H6.99992Z"
                fill="#FF9330"
              />
            </svg>
          </div>
          <div class="w-56 text-xs text-body-color">
            This address is undeliverable according to the USPS.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UndeliverableAddress',
  props: {
    insight: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      undeliverableStreet1: this.aggregateStreet1(),
    };
  },
  methods: {
    capitalizeWords: function (str) {
      return str
        .toLowerCase()
        .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
        .replace('Llc', 'LLC');
    },
    aggregateStreet1: function () {
      var aggregated = [];
      this.insight.associated_addresses.forEach(function (el) {
        aggregated.push(el.street1);
      });
      return aggregated;
    },
    aggregatedMatch: function (address) {
      var match = false;
      this.insight.associated_addresses.forEach(function (el) {
        if (el.street1 == address.street1) {
          match = true;
        }
      });
      return match;
    },
  },
};
</script>
